/**
 * ARRASTAR PONTOS — a figura que o aluno ajusta com as mãos.
 *
 * O cenário (malha, eixo, reta, gráfico) é uma `Scene` comum; por cima dela vai uma
 * camada própria com os pontos arrastáveis. Os pontos encaixam nos cruzamentos de uma
 * malha: sem isso, "quase no lugar" viraria discussão de pixels, e o item mediria
 * pontaria.
 *
 * Três desenhos para três usos:
 *  - 'figura': vértices ligados por segmentos (a simetria, a ampliação);
 *  - 'marcadores': alfinetes com haste até a base (a reta numérica), cada um na sua
 *    faixa, para os nomes não se atropelarem;
 *  - 'barras': colunas que sobem da base até o ponto (montar um gráfico).
 *
 * E uma TRAVA opcional: o ponto só anda em x (reta) ou só em y (coluna).
 *
 * Três jeitos de mover, todos com o mesmo efeito: arrastar com o mouse, arrastar com o
 * dedo e, com o ponto em foco, as setas do teclado — um passo da malha por toque.
 */

import { formatBR } from '../core/num.ts';
import type { Vec2 } from '../geom/vec.ts';
import { conversor } from './coords.ts';
import type { Shape } from '../render/scene.ts';
import { scene } from '../render/scene.ts';
import { renderScene } from '../render/svg.ts';
import type { DragPointsInteraction } from '../schema/item.ts';
import { toPlainText } from '../schema/richtext.ts';

const NS = 'http://www.w3.org/2000/svg';

const SETAS: Readonly<Record<string, readonly [number, number]>> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, 1],
  ArrowDown: [0, -1],
};

export interface Arrastador {
  readonly element: HTMLElement;
  posicoes(): Vec2[];
  movimentos(): number;
  travar(): void;
  dispose(): void;
}

export interface OpcoesArrastador {
  readonly bloqueado?: boolean;
  /** Marcas desenhadas sob os pontos — as da revisão. */
  readonly extras?: readonly Shape[];
  readonly aoMover?: (indice: number, p: Vec2) => void;
}

export function criarArrastador(
  inter: DragPointsInteraction,
  iniciais: readonly Vec2[],
  opts: OpcoesArrastador = {},
): Arrastador {
  const vb = inter.viewBox;
  const passo = inter.passo;
  const unidade = inter.unidade ?? passo;
  const desenho = inter.desenho ?? 'figura';
  const base = inter.base ?? 0;
  const pos: Vec2[] = iniciais.map((p) => ({ x: p.x, y: p.y }));
  // A trava prende a coordenada no valor de PARTIDA de cada ponto (a faixa do marcador,
  // a posição da coluna), e não num valor comum a todos.
  const fixo: Vec2[] = inter.pontos.map((p) => ({ x: p.inicial.x, y: p.inicial.y }));
  let movs = 0;
  let bloqueado = opts.bloqueado ?? false;
  const fixos = new Set(inter.fixos ?? []);

  const root = document.createElement('div');
  root.className = `arrastar desenho-${desenho}${bloqueado ? ' is-bloqueado' : ''}`;
  const svg = renderScene(
    scene([...inter.backdrop, ...(opts.extras ?? [])], {
      a11y: {
        title: 'Figura com pontos para arrastar',
        desc: `${inter.descricao} ${toPlainText(inter.alvo.describe)}`,
      },
      viewBox: vb,
    }),
  );
  root.appendChild(svg);

  // A camada fica fora do grupo invertido da cena: y é convertido aqui, e o texto dos
  // nomes não sai de cabeça para baixo.
  const { yTela, paraModelo } = conversor(svg, vb);
  const el = <K extends keyof SVGElementTagNameMap>(
    tag: K,
    cls: string,
  ): SVGElementTagNameMap[K] => {
    const e = document.createElementNS(NS, tag);
    e.setAttribute('class', cls);
    return e;
  };

  // Alvos de toque com tamanho mínimo: numa reta dividida em décimos, o passo da malha
  // é pequeno demais para um dedo.
  const raioAlvo = Math.max(passo * 0.5, vb.w * 0.03);
  const raioPonto = Math.max(passo * 0.19, vb.w * 0.011);

  const camada = el('g', 'arrastar-camada');
  const figura = el('polygon', 'arrastar-figura');
  if (desenho === 'figura') camada.appendChild(figura);
  const linhas =
    desenho === 'figura'
      ? inter.ligacoes.map(() => camada.appendChild(el('line', 'arrastar-ligacao')))
      : [];
  const hastes =
    desenho === 'marcadores'
      ? inter.pontos.map(() => camada.appendChild(el('line', 'arrastar-haste')))
      : [];
  const barras =
    desenho === 'barras'
      ? inter.pontos.map(() => camada.appendChild(el('rect', 'arrastar-barra')))
      : [];
  const alcas = inter.pontos.map((p, i) => {
    const g = el('g', fixos.has(i) ? 'arrastar-alca is-fixo' : 'arrastar-alca');
    g.setAttribute('tabindex', bloqueado || fixos.has(i) ? '-1' : '0');
    g.setAttribute('role', 'button');
    const alvo = el('circle', 'alvo');
    alvo.setAttribute('r', String(raioAlvo));
    const ponto = el('circle', 'ponto');
    ponto.setAttribute('r', String(raioPonto));
    const rot = el('text', 'arrastar-rotulo');
    rot.setAttribute('text-anchor', 'middle');
    rot.setAttribute('dominant-baseline', desenho === 'marcadores' ? 'auto' : 'central');
    // Numa coluna, o nome estaria no gráfico duas vezes: fica só o da categoria.
    rot.textContent = desenho === 'barras' ? '' : p.nome;
    g.append(alvo, ponto, rot);
    camada.appendChild(g);
    return { g, alvo, ponto, rot };
  });
  svg.appendChild(camada);

  const fmt = (v: number): string => formatBR(Math.round(v * 100) / 100);

  function descrever(i: number, p: Vec2): string {
    const nome = inter.pontos[i]?.nome ?? '';
    const onde =
      inter.trava === 'x'
        ? `na posição ${fmt(p.x / unidade)}`
        : inter.trava === 'y'
          ? `com altura ${fmt((p.y - base) / unidade)}`
          : `na posição (${fmt(p.x / unidade)}, ${fmt(p.y / unidade)}) da malha`;
    if (fixos.has(i)) return `Ponto ${nome}, fixo, ${onde}.`;
    return `${desenho === 'barras' ? 'Coluna' : 'Ponto'} ${nome}, ${onde}. Arraste, ou use as setas do teclado para mover.`;
  }

  function desenhar(): void {
    if (desenho === 'figura') {
      figura.setAttribute('points', pos.map((p) => `${p.x},${yTela(p.y)}`).join(' '));
      inter.ligacoes.forEach(([a, b], k) => {
        const l = linhas[k];
        const pa = pos[a];
        const pb = pos[b];
        if (!l || !pa || !pb) return;
        l.setAttribute('x1', String(pa.x));
        l.setAttribute('y1', String(yTela(pa.y)));
        l.setAttribute('x2', String(pb.x));
        l.setAttribute('y2', String(yTela(pb.y)));
      });
    }
    const c = {
      x: pos.reduce((s, p) => s + p.x, 0) / Math.max(pos.length, 1),
      y: pos.reduce((s, p) => s + p.y, 0) / Math.max(pos.length, 1),
    };
    alcas.forEach((h, i) => {
      const p = pos[i] as Vec2;
      for (const circ of [h.alvo, h.ponto]) {
        circ.setAttribute('cx', String(p.x));
        circ.setAttribute('cy', String(yTela(p.y)));
      }
      if (desenho === 'marcadores') {
        const haste = hastes[i];
        haste?.setAttribute('x1', String(p.x));
        haste?.setAttribute('y1', String(yTela(p.y)));
        haste?.setAttribute('x2', String(p.x));
        haste?.setAttribute('y2', String(yTela(base)));
        // O nome fica em cima do alfinete.
        h.rot.setAttribute('x', String(p.x));
        h.rot.setAttribute('y', String(yTela(p.y) - raioPonto - vb.h * 0.03));
      } else if (desenho === 'barras') {
        const r = barras[i];
        const w = inter.larguraBarra ?? passo;
        const topo = Math.max(p.y, base);
        r?.setAttribute('x', String(p.x - w / 2));
        r?.setAttribute('y', String(yTela(topo)));
        r?.setAttribute('width', String(w));
        r?.setAttribute('height', String(Math.max(0, topo - base)));
      } else {
        // O nome fica do lado de FORA da figura, longe do centro dela.
        const dx = p.x - c.x;
        const dy = p.y - c.y;
        const L = Math.hypot(dx, dy) || 1;
        h.rot.setAttribute('x', String(p.x + (dx / L) * passo * 0.6));
        h.rot.setAttribute('y', String(yTela(p.y + (dy / L) * passo * 0.6)));
      }
      h.g.setAttribute('aria-label', descrever(i, p));
    });
  }

  const margem = passo * 0.3;
  const lim = inter.limites ?? {
    x: vb.x + margem,
    y: vb.y + margem,
    w: vb.w - 2 * margem,
    h: vb.h - 2 * margem,
  };
  const minX = Math.ceil(lim.x / passo - 1e-9) * passo;
  const maxX = Math.floor((lim.x + lim.w) / passo + 1e-9) * passo;
  const minY = Math.ceil(lim.y / passo - 1e-9) * passo;
  const maxY = Math.floor((lim.y + lim.h) / passo + 1e-9) * passo;
  const limitar = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));
  const encaixar = (p: Vec2, i: number): Vec2 => {
    const f = fixo[i] as Vec2;
    const x = inter.trava === 'y' ? f.x : limitar(Math.round(p.x / passo) * passo, minX, maxX);
    const y = inter.trava === 'x' ? f.y : limitar(Math.round(p.y / passo) * passo, minY, maxY);
    return { x, y };
  };

  let ativo: { readonly i: number; readonly inicio: Vec2 } | null = null;

  alcas.forEach((h, i) => {
    h.g.addEventListener('pointerdown', (e) => {
      if (bloqueado || fixos.has(i)) return;
      e.preventDefault();
      ativo = { i, inicio: { ...(pos[i] as Vec2) } };
      h.g.classList.add('is-arrastando');
      try {
        h.g.setPointerCapture(e.pointerId);
      } catch {
        // sem captura (ambiente de teste): o arrasto segue pelos eventos do próprio ponto
      }
    });
    h.g.addEventListener('pointermove', (e) => {
      if (bloqueado || ativo?.i !== i) return;
      const q = encaixar(paraModelo(e), i);
      const atual = pos[i] as Vec2;
      if (q.x !== atual.x || q.y !== atual.y) {
        pos[i] = q;
        desenhar();
      }
    });
    const soltar = (): void => {
      if (ativo?.i !== i) return;
      const p = pos[i] as Vec2;
      if (p.x !== ativo.inicio.x || p.y !== ativo.inicio.y) {
        movs++;
        opts.aoMover?.(i, p);
      }
      ativo = null;
      h.g.classList.remove('is-arrastando');
    };
    h.g.addEventListener('pointerup', soltar);
    h.g.addEventListener('pointercancel', soltar);
    h.g.addEventListener('keydown', (e) => {
      if (bloqueado || fixos.has(i)) return;
      const d = SETAS[e.key];
      if (!d) return;
      if ((inter.trava === 'x' && d[1] !== 0) || (inter.trava === 'y' && d[0] !== 0)) return;
      e.preventDefault();
      const p = pos[i] as Vec2;
      const q = encaixar({ x: p.x + d[0] * passo, y: p.y + d[1] * passo }, i);
      if (q.x === p.x && q.y === p.y) return;
      pos[i] = q;
      movs++;
      desenhar();
      opts.aoMover?.(i, q);
    });
  });

  desenhar();

  const dica = document.createElement('p');
  dica.className = 'arrastar-dica';
  // O comando já diz o que arrastar e até onde; a dica fica com o que ele não diz — o
  // teclado e, na malha, o encaixe nos cruzamentos.
  const dicaTxt =
    desenho === 'barras'
      ? 'Com uma coluna selecionada, as setas do teclado também a movem.'
      : desenho === 'marcadores'
        ? 'Com um marcador selecionado, as setas do teclado também o movem.'
        : 'Os pontos se encaixam nos cruzamentos da malha. Com um ponto selecionado, as setas do teclado também o movem.';
  const travadoTxt =
    desenho === 'barras'
      ? 'Resposta enviada: as colunas não podem mais ser mudadas.'
      : desenho === 'marcadores'
        ? 'Resposta enviada: os marcadores não podem mais ser movidos.'
        : 'Resposta enviada: os pontos não podem mais ser movidos.';
  dica.textContent = bloqueado ? travadoTxt : dicaTxt;
  root.appendChild(dica);

  return {
    element: root,
    posicoes: () => pos.map((p) => ({ x: p.x, y: p.y })),
    movimentos: () => movs,
    travar() {
      bloqueado = true;
      root.classList.add('is-bloqueado');
      for (const h of alcas) h.g.setAttribute('tabindex', '-1');
      dica.textContent = travadoTxt;
    },
    dispose() {
      ativo = null;
    },
  };
}
